import styled, { keyframes } from 'styled-components';

import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { decodeHTML, postFeedbackForm } from '../../../utility/common';
import {contentClick, FormStart} from "../../../common/Analytics/GAWrapper.jsx";

import {
  Flex,
  MOBILE_BREAKPOINT,
} from '../../../components/common/StyledComponent/CommonStyledComponent';
import { Icon } from '@vds/icons';

const MAX_CHAR_COUNT = 1000;

const panelDropIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-28px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const panelRiseOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-28px);
  }
`;

const PanelShell = styled.section`
  background: rgba(255, 255, 255, 1);
  border-radius: 16px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
  animation: ${(props) => (props.$isClosing ? panelRiseOut : panelDropIn)} 0.35s
    cubic-bezier(0.22, 1, 0.36, 1) forwards;

  // @media (max-width: ${MOBILE_BREAKPOINT}px) {
  //   padding: 16px;
  // }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TagButton = styled.button`
  border-radius: 9999px;
  border: 1px solid #000000;
  background-color: ${(props) => (props.$isSelected ? '#000000' : 'transparent')};
  color: ${(props) => (props.$isSelected ? '#ffffff' : '#000000')};
  padding: 8px 16px;
  cursor: pointer;
  height: 32px;
  box-sizing: border-box;
  transition:
    background-color 220ms ease,
    color 220ms ease,
    opacity 420ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 420ms cubic-bezier(0.22, 1, 0.36, 1),
    border-width 160ms ease;

  &:hover {
    border: 1px solid #000000;
    box-shadow: ${(props) =>
      props.$isSelected ? 'none' : 'inset 0 0 0 1px #000000'};
  }
`;

const DetailsInput = styled.textarea`
  width: 100%;
  min-height: 88px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 1);
  background: #ffffff;
  box-sizing: border-box;
  padding: 12px;
  font-size: 14px;
  resize: none;

  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const SubmitButton = styled.button`
  margin-top: 16px;
  height: 32px;
  border-radius: 9999px;
  border: none;
  padding: 8px 16px;
  background: ${(props) => (props.disabled ? '#cecece' : '#000000')};
  color: #ffffff;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transform: translateZ(0);
  will-change: transform, opacity;
  transition:
    opacity 260ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1);

  &:not(:disabled):hover {
    box-shadow: 0 0 0 1px #000;
    transform: none !important;
  }
`;

const AskAIFeedbackPanel = ({ onClose, isClosing, onAnimationEnd, onSubmit, onSubmitError, selectedThumb, vegasId , formId}) => {
  const askAiData = useSelector((state) => state.Aem?.aemAiSearchData);
  const feedbackSection = askAiData?.resultsPageTexts?.feedbackSection;
  const feedbackPills = selectedThumb === 'up'
    ? feedbackSection?.psFeedbckPills
    : feedbackSection?.ngFeedbckPills;
  const FEEDBACK_TAGS = useMemo(
    () => (feedbackPills || []).map((pill) => pill.feedbckLbl),
    [feedbackPills]
  );
  const [selectedTags, setSelectedTags] = useState([]);
  const [details, setDetails] = useState('');
  const hasStartedFormRef = useRef(false);
  // const hasFiredTypingEventRef = useRef(false);
  const canSubmit = useMemo(
    () => details.trim().length > 0 || selectedTags.length > 0,
    [details, selectedTags]
  );

  const toggleTag = (tag) => {
    contentClick(
        tag,
        window.location.href,
        '', 
        'feedback_form_tag',
      );
      if (!hasStartedFormRef.current) {
        const formType = selectedThumb === 'up' ? 'Postive_AISearch_Feedback' : 'Negative_AISearch_Feedback';
        FormStart('AISearch_Feedback_form', [formId], formType);
        hasStartedFormRef.current = true;
      }
    setSelectedTags((prevTags) => {
      if (prevTags.includes(tag)) {
        return prevTags.filter((prevTag) => prevTag !== tag);
      }
      return [...prevTags, tag];
    });
  };

  const onDetailsChange = (event) => {
    setDetails(event.target.value.slice(0, MAX_CHAR_COUNT));
    if (!hasStartedFormRef.current) {
       const formType = selectedThumb === 'up' ? 'Postive_AISearch_Feedback' : 'Negative_AISearch_Feedback';
     
      FormStart('AISearch_Feedback_form',  [formId], formType);
      hasStartedFormRef.current = true;
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittedRef = useRef(false);

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting || isSubmittedRef.current) {
      return;
    }

    setIsSubmitting(true);
    try {
      await postFeedbackForm({
        vegasId,
        topic: selectedTags,
        description: details.trim(),
        type: selectedThumb === 'up' ? 'Positive' : 'Negative',
      });
      isSubmittedRef.current = true;
      onSubmit?.(selectedTags);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      onSubmitError?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PanelShell $isClosing={isClosing} onAnimationEnd={onAnimationEnd}>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Flex flexDirection="column" gap="8px">
          <div className="vds-title-xs font-eDS-bold">
            {decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.ratingFeedbckLabel)}{' '}
            <span className="vds-title-2xs">{askAiData?.resultsPageTexts?.feedbackSection?.optText}</span>
          </div>
          <div className="vds-body-md">
            {decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.feedbackImproveTxt)}
          </div>
        </Flex>
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label="Close feedback"
        >
          <Icon name="close" size={20} />
        </CloseButton>
      </Flex>

      <div className="vds-body-sm mt-6X mb-2X">{decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.selectAllTxt)}</div>
      <Flex gap="12px" flexWrap='wrap'>
        {FEEDBACK_TAGS.map((tag) => {
          const isSelected = selectedTags.includes(tag);

          return (
            <TagButton
              key={tag}
              type="button"
              $isSelected={isSelected}
              onClick={() => toggleTag(tag)}
              aria-pressed={isSelected}
            >
              <div className="vds-body-sm font-eTX-bold">{tag}</div>
            </TagButton>
          );
        })}
      </Flex>

      <div className="mb-1X mt-6X vds-body-sm">
        {decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.additionalDltsLbl)}
      </div>
      <DetailsInput
        aria-label={decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.additionalDltsLbl)}
        value={details}
        onChange={onDetailsChange}
        maxLength={MAX_CHAR_COUNT}
      />
      <div className="n-textRight vds-body-sm">
        {details.length}/{MAX_CHAR_COUNT}
      </div>

      <SubmitButton type="button" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
        <div className="vds-body-sm font-eTX-bold">{decodeHTML(askAiData?.resultsPageTexts?.feedbackSection?.submitBtnTxt)}</div>
      </SubmitButton>
    </PanelShell>
  );
};

export default AskAIFeedbackPanel;
